import { Injectable } from '@angular/core';
import { MaterialCssVarsService } from 'angular-material-css-vars';
import * as MaterialCss from 'angular-material-css-vars';
import { BrandPalette } from '@models/branding/brand-palette.model';
import { CompanyBrandSetting } from '@app/models/company/company-brand-settings.model';

const DefaultColors = {
  brandColor: '57A7B5',
  interactiveElementsColor: '3D757F',
  mainButtonColor: 'FEC22F',
  buttonTextColor: '000000',
  whiteColor: 'FFFFFF',
};

@Injectable({
  providedIn: 'root',
})
export class ColorService {
  private defaultBrandColor = true;

  constructor(private materialCssVarsService: MaterialCssVarsService) {
    this.setDefaultBrandColor();
  }

  public isDefaultBrandColor(): boolean {
    return this.defaultBrandColor;
  }

  public setDefaultBrandColor() {
    this.materialCssVarsService.setAutoContrastEnabled(false);
    this.materialCssVarsService.setPrimaryColor(DefaultColors.brandColor);

    this.materialCssVarsService.setAccentColor(
      '#' + DefaultColors.interactiveElementsColor,
    );

    this.materialCssVarsService.setVariable(
      '--background-raised-button' as any,
      this.hexToRGB(DefaultColors.mainButtonColor),
    );

    this.materialCssVarsService.setVariable(
      MaterialCss.MaterialCssVariables.Accent50,
      this.hexToRGB(DefaultColors.buttonTextColor),
    );

    this.materialCssVarsService.setVariable(
      '--mat-button-outlined-label-text-color' as any,
      `rgb(${this.hexToRGB(DefaultColors.buttonTextColor)})`,
    );

    this.materialCssVarsService.setVariable(
      '--mdc-outlined-text-field-focus-outline-color' as any,
      this.hexToRGB(DefaultColors.interactiveElementsColor),
    );

    this.materialCssVarsService.setVariable(
      '--mdc-outlined-button-disabled-outline-color' as any,
      `rgba(${this.hexToRGBA(DefaultColors.interactiveElementsColor, 0.5)})`,
    );
  }

  public setBrandColor(brandPalette: BrandPalette | CompanyBrandSetting): void {
    this.defaultBrandColor =
      brandPalette?.brandColor.toLowerCase() ===
      DefaultColors.brandColor.toLowerCase();

    // disable auto contrast on individual chosen company color
    this.materialCssVarsService.setAutoContrastEnabled(false);
    this.materialCssVarsService.setPrimaryColor(brandPalette?.brandColor);

    this.materialCssVarsService.setAccentColor(
      '#' + brandPalette?.interactiveElementsColor,
    );

    this.materialCssVarsService.setVariable(
      '--background-raised-button' as any,
      this.hexToRGB(brandPalette?.mainButtonColor),
    );

    this.materialCssVarsService.setVariable(
      MaterialCss.MaterialCssVariables.Accent50,
      this.hexToRGB(brandPalette?.buttonTextColor),
    );

    this.materialCssVarsService.setVariable(
      '--mat-button-outlined-label-text-color' as any,
      `rgb(${this.hexToRGB(brandPalette?.buttonTextColor)})`,
    );

    this.materialCssVarsService.setVariable(
      '--mdc-outlined-text-field-focus-outline-color' as any,
      `rgba(${this.hexToRGBA(brandPalette?.interactiveElementsColor, 0.5)})`,
    );

    this.materialCssVarsService.setVariable(
      '--mdc-outlined-button-disabled-outline-color' as any,
      `rgba(${this.hexToRGBA(brandPalette?.interactiveElementsColor, 0.5)})`,
    );
  }

  private hexToRGB(hex: string): string {
    if (hex.length === 3) {
      hex = hex.replace(/.{1,1}/g, '$&$&');
    }

    const rgb = hex.match(/.{1,2}/g) as Array<string>;

    const rgbArray = [
      parseInt(rgb[0], 16),
      parseInt(rgb[1], 16),
      parseInt(rgb[2], 16),
    ];

    return rgbArray.join(', ');
  }

  private hexToRGBA(hex: string, opacity: number): string {
    const rgbaArray = this.hexToRGB(hex).split(', ');
    rgbaArray.push(opacity.toString());

    return rgbaArray.join(', ');
  }
}
